import {
  STORAGE_KEY_ENUM,
  EFFORT_MAIL_CONTACT_TYPE_ENUM,
  PROJECT_STEP_STATUS_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  getEffortMailContactTypeTranslated,
} from 'enums';
import {
  useRef,
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
  SyntheticEvent,
  MutableRefObject,
  PointerEvent as RPointerEvent,
  KeyboardEvent as RKeyboardEvent,
} from 'react';
import {
  StyledBox,
  EditToolbar,
  ShowVersions,
  StickyActions,
  ZebraDataGrid,
  CustomToolbar,
  GridEditDateCell,
  ProductsEditTable,
  PermissionHandler,
  GridFilterDateInput,
  RichTextOutlinedField,
  RelatedContactsEditTable,
  ResizableRichTextOutlinedField,
} from 'components';
import {
  Box,
  Alert,
  Radio,
  Button,
  Dialog,
  Switch,
  Divider,
  Backdrop,
  useTheme,
  TextField,
  FormLabel,
  Accordion,
  RadioGroup,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
  Grid2 as Grid,
  svgIconClasses,
  CircularProgress,
  FormControlLabel,
  AccordionDetails,
  AccordionSummary,
  DialogContentText,
} from '@mui/material';
import {
  GridRowId,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowsProp,
  GridColTypeDef,
  GridRowModesModel,
  GridEventListener,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowEditStopReasons,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid-premium';
import {
  modernDangerButtonSx,
  modernDashboardSapSx,
  modernDashboardActionsSx,
  modernDashboardContentSx,
} from './modern-dashboard-styles';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  selectConnectionTypes,
  selectConnectionTypesIsEmpty,
  selectConnectionTypesLoading,
} from 'store/features/connection-types/selector';
import CancelIcon from '@mui/icons-material/Close';
import { gray } from 'providers/theme/themePrimitives';
import { FormattedMessage, useIntl } from 'react-intl';
import RenderEditItem from 'components/render-edit-item';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import gibTypesActions from 'store/features/gib-types/actions';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import usedBanksActions from 'store/features/used-banks/actions';
import ModernDashboardHeader from './components/modern-dashboard-header';
import ModernDashboardSection from './components/modern-dashboard-section';
import connectionTypesActions from 'store/features/connection-types/actions';
import environmentTypesActions from 'store/features/environment-types/actions';
import ModernDashboardEmptyState from './components/modern-dashboard-empty-state';
import { ConnectionType, GibType, ShortBook, UsedBank, User } from 'library/types';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectUpdateCompanyName, selectUpdateFullBook } from 'store/features/permissions/selector';
import { selectGibTypes, selectGibTypesIsEmpty, selectGibTypesLoading } from 'store/features/gib-types/selector';
import {
  TAB_SIZE,
  LocalStorage,
  insertTabAtSelection,
  renderFormattedDate,
  renderDataGridLocaleText,
  renderAutoCompleteLocaleText,
} from 'utils';
import { selectEnvironmentTypes, selectEnvironmentTypesIsEmpty } from 'store/features/environment-types/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty, selectUsedBanksLoading } from 'store/features/used-banks/selector';

const language = LocalStorage.getItem(STORAGE_KEY_ENUM.LANGUAGE) || 'tr';

export type CompanyToBeEdited = {
  code: string;
  vkn: string;
  title: string;
  dateOfAnnulment: Dayjs | string | null;
};

export type RelatedContactToBeEdited = {
  nameSurname: string;
  email: string;
  phoneNumber: string;
};

export type ProductToBeEdited = {
  id: number;
  environmentTypeId: number | null;
  liveDate: Dayjs | string | null;
  isOnHold: boolean;
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM | null;
  relatedContactId: number | null;
  alternativeRelatedContactId: number | null;
  notes: string | null;
  onHoldTotalDays: number | null;
};

export type Book = {
  id: number;
  companyName: string | null;
  projects: ProductToBeEdited[];
  connectTypeId: number | null;
  gibTypeId: number | null;
  usedBankIds: number[];
  companies: CompanyToBeEdited[];
  apiInformation: string | null;
  vpnInformation: string | null;
  serviceApiInformation: string | null;
  rfcUserInformation: string | null;
  serverConnectionInformation: string | null;
  serverExternalIp: string | null;
  financialSeal: string | null;
  sapDev: string | null;
  sapTest: string | null;
  sapProd: string | null;
  note: string | null;
  relatedContacts: RelatedContactToBeEdited[] | null;
  effortMailContactType: EFFORT_MAIL_CONTACT_TYPE_ENUM;
  isCancelled: boolean;
  userNote: string | null;
};

const initialState: Book = {
  id: 9999,
  companyName: null,
  projects: [],
  connectTypeId: null,
  gibTypeId: null,
  usedBankIds: [],
  companies: [],
  apiInformation: null,
  vpnInformation: null,
  serviceApiInformation: null,
  rfcUserInformation: null,
  serverConnectionInformation: null,
  serverExternalIp: null,
  financialSeal: null,
  sapDev: null,
  sapTest: null,
  sapProd: null,
  note: null,
  relatedContacts: [],
  effortMailContactType: EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER,
  isCancelled: false,
  userNote: null,
};

const initialRows: GridRowsProp = [
  {
    id: crypto.randomUUID(),
    code: language === 'tr' ? 'Kod' : 'Code',
    vkn: language === 'tr' ? 'Vkn' : 'Tax Id',
    title: language === 'tr' ? 'Ünvan' : 'Title',
    dateOfAnnulment: null,
  },
];

const initialRowsRelatedContacts: GridRowsProp = [];

const initialRowsProducts: GridRowsProp = [
  {
    id: crypto.randomUUID(),
    projectId: '',
    environmentTypeId: null,
    liveDate: null,
    isOnHold: false,
    priority: null,
    relatedContactId: null,
    alternativeRelatedContactId: null,
    notes: '',
    onHoldTotalDays: null,
  },
];

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    const v = params.value;

    const dayjsValue = v instanceof Date ? dayjs(v) : v ? dayjs(v) : null;

    return <GridEditDateCell {...params} value={dayjsValue} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
};

const MIN_PERCENT = 10;
type BookListStatus = 'loading' | 'ready' | 'error';
type ProjectCompletionStatus = 'idle' | 'loading' | 'ready' | 'error';

const Dashboard = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const bookRequestIdRef = useRef(0);
  const { enqueueSnackbar } = useSnackbar();
  const sapRef = useRef<HTMLDivElement | null>(null);
  const vpnRef = useRef<HTMLDivElement | null>(null);
  const serverRef = useRef<HTMLDivElement | null>(null);

  // redux hooks
  const dispatch = useAppDispatch();

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const connectionTypes = useAppSelector(selectConnectionTypes);
  const loadingConnectionTypes = useAppSelector(selectConnectionTypesLoading);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  const gibTypes = useAppSelector(selectGibTypes);
  const loadingGibTypes = useAppSelector(selectGibTypesLoading);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  const usedBanks = useAppSelector(selectUsedBanks);
  const loadingUsedBanks = useAppSelector(selectUsedBanksLoading);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  const updateFullBook = useAppSelector(selectUpdateFullBook);
  const updateCompanyName = useAppSelector(selectUpdateCompanyName);

  // useState hooks
  const [sizes, setSizes] = useState({
    col1: 33,
    col2: 34,
    col3: 33,
  });
  const [edit, setEdit] = useState(false);
  const [rows, setRows] = useState(initialRows);
  const [users, setUsers] = useState<User[]>([]);
  const [scrollUp, setScrollUp] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [books, setBooks] = useState<ShortBook[]>([]);
  const [bookListStatus, setBookListStatus] = useState<BookListStatus>('loading');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bookDetailsError, setBookDetailsError] = useState(false);
  const [openShowVersions, setOpenShowVersions] = useState(false);
  const [editFormParams, setEditFormParams] = useState(initialState);
  const [loadingBookDetails, setLoadingBookDetails] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book>(initialState);
  const [rowsProducts, setRowsProducts] = useState(initialRowsProducts);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedUsedBanks, setSelectedUsedBanks] = useState<UsedBank[]>([]);
  const [completedProjectIds, setCompletedProjectIds] = useState<number[]>([]);
  const [projectCompletionStatus, setProjectCompletionStatus] = useState<ProjectCompletionStatus>('idle');
  const [pendingBookTitle, setPendingBookTitle] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedGibType, setSelectedGibType] = useState<ConnectionType | null>(null);
  const [rowModesModelProducts, setRowModesModelProducts] = useState<GridRowModesModel>({});
  const [rowsRelatedContacts, setRowsRelatedContacts] = useState(initialRowsRelatedContacts);
  const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType | null>(null);
  const [rowModesModelRelatedContacts, setRowModesModelRelatedContacts] = useState<GridRowModesModel>({});

  // dynamic variables
  const loadingBooks = bookListStatus === 'loading';
  const isSelectedBookPending = selectedCompanyId !== null && selectedBook.id !== selectedCompanyId;
  const isBookDetailsLoading = loadingBookDetails || isSelectedBookPending;
  const isBookDetailsReady = selectedCompanyId !== null && !isBookDetailsLoading;
  const isProductCompletionStatusReady = projectCompletionStatus === 'ready';
  const incompleteAdaptation =
    isEmptyProducts || isEmptyConnectionTypes || isEmptyGibTypes || isEmptyUsedBanks || isEmptyEnvironmentTypes;

  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      flex: 0.25,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'code',
      headerName: intl.formatMessage({ id: 'project.table.code', defaultMessage: 'Şirket Kodu' }),
      flex: 1,
      editable: edit,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'vkn',
      headerName: intl.formatMessage({ id: 'project.table.vkn', defaultMessage: 'Vkn' }),
      flex: 1,
      editable: edit,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'title',
      headerName: intl.formatMessage({ id: 'project.table.title', defaultMessage: 'Ünvan' }),
      flex: 1,
      editable: edit,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'dateOfAnnulment',
      ...dateColumnType,
      // 1) We convert raw value to Date for Excel Export and sorting
      valueGetter: (value) => {
        if (!value) return null;
        return new Date(value); // Excel export will read THIS
      },

      // 2) UI input -> Date conversions
      valueParser: (value) => {
        if (!value) return null;
        return new Date(value);
      },

      // 3) (string) for UI rendering
      valueFormatter: (value) => {
        return value ? renderFormattedDate(value) : '';
      },
      headerName: intl.formatMessage({ id: 'project.table.dateOfAnnulment', defaultMessage: 'Fesih Tarihi' }),
      flex: 0.5,
      editable: edit,
    },
    edit
      ? {
          field: 'actions',
          type: 'actions',
          headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
          width: 100,
          cellClassName: 'actions',
          getActions: ({ id }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
                  sx={{
                    color: 'primary.main',
                  }}
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ];
            }

            return [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
                onClick={handleDeleteClick(id)}
                color="inherit"
              />,
            ];
          },
        }
      : {
          field: 'none',
        },
  ];

  // async helper functions
  const getCompanies = async () => {
    try {
      setBookListStatus('loading');

      const response = await apiService.getCompanies();

      const duplicateArr = [...response.data];
      const sortedData = duplicateArr.sort((a, b) => a.title.localeCompare(b.title));

      setBooks(sortedData);
      setBookListStatus('ready');
    } catch {
      setBookListStatus('error');
    }
  };

  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();
      if (response.isSuccess) {
        setUsers(response.data);
      }
    } catch {}
  };

  const getSpecificBook = useCallback(
    async (id: number) => {
      const requestId = ++bookRequestIdRef.current;
      setLoadingBookDetails(true);
      setBookDetailsError(false);
      setCompletedProjectIds([]);
      setProjectCompletionStatus('loading');

      try {
        const response = await apiService.getSpecificBook(id);
        if (requestId !== bookRequestIdRef.current) return;

        setPendingBookTitle(null);
        setSelectedBook(response.data);
        setEditFormParams(response.data);

        // Companies
        const formattedCompanies = response.data.companies.map((company) => ({
          id: crypto.randomUUID(),
          code: company.code,
          vkn: company.vkn,
          title: company.title,
          dateOfAnnulment: company.dateOfAnnulment ? dayjs(company.dateOfAnnulment) : null,
        }));

        setRows(formattedCompanies);

        // Related Contacts
        const formattedRelatedContacts =
          response.data.relatedContacts?.map((relatedContact) => ({
            id: crypto.randomUUID(),
            nameSurname: relatedContact.nameSurname,
            email: relatedContact.email,
            phoneNumber: relatedContact.phoneNumber,
          })) ?? [];

        setRowsRelatedContacts(formattedRelatedContacts);

        // Products
        const formattedProducts = response.data.projects.map((project) => ({
          id: crypto.randomUUID(),
          projectId: project.id,
          environmentTypeId: project.environmentTypeId,
          liveDate: project.liveDate ? dayjs(project.liveDate) : null,
          isOnHold: project.isOnHold,
          priority: project.priority,
          relatedContactId: project.relatedContactId,
          alternativeRelatedContactId: project.alternativeRelatedContactId,
          notes: project.notes,
          onHoldTotalDays: project.onHoldTotalDays,
        }));

        setRowsProducts(formattedProducts);

        // Connection Type
        const connectionType = connectionTypes.find(
          (connectionType) => connectionType.id === response.data.connectTypeId
        );

        setSelectedConnectionType(connectionType || null);

        // GIB Type
        const gibType = gibTypes.find((gibType) => gibType.id === response.data.gibTypeId);

        setSelectedGibType(gibType || null);

        // Used Banks
        let sUsedBanks: UsedBank[] = [];
        response.data.usedBankIds.forEach((usedBankId) => {
          const usedBank = usedBanks.find((usedBank) => usedBank.id === usedBankId);

          if (usedBank) {
            sUsedBanks.push(usedBank);
          }
        });

        setSelectedUsedBanks(sUsedBanks);
        setLoadingBookDetails(false);

        // Fetch steps to determine which projects are 'COMPLETED'
        try {
          const stepsResponse = await apiService.getProjectSteps({
            statusType: null,
            projectId: null,
            bookId: id,
            contactId: null,
          });
          if (requestId !== bookRequestIdRef.current) return;

          if (stepsResponse.isSuccess) {
            const steps = stepsResponse.data;
            const completedIds: number[] = [];

            response.data.projects.forEach((project) => {
              const projectSteps = steps.filter((step) => step.projectId === project.id);
              if (projectSteps.length > 0) {
                const allCompleted = projectSteps.every(
                  (step) => step.status === PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED
                );
                if (allCompleted) {
                  completedIds.push(project.id);
                }
              }
            });

            setCompletedProjectIds(completedIds);
            setProjectCompletionStatus('ready');
          } else {
            setCompletedProjectIds([]);
            setProjectCompletionStatus('error');
          }
        } catch {
          if (requestId !== bookRequestIdRef.current) return;
          setCompletedProjectIds([]);
          setProjectCompletionStatus('error');
        }
      } catch {
        if (requestId !== bookRequestIdRef.current) return;
        setPendingBookTitle(null);
        setBookDetailsError(true);
        setProjectCompletionStatus('idle');
        setSelectedCompanyId(null);
      } finally {
        if (requestId === bookRequestIdRef.current) setLoadingBookDetails(false);
      }
    },
    [connectionTypes, gibTypes, usedBanks]
  );

  const onClickSaveHandler = async () => {
    // Empty Company Name control
    if (!editFormParams.companyName)
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredCompanyCode" defaultMessage="Firma boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Companies
    const isViewModeForCompanies = Object.keys(rowModesModel).length === 0;

    if (!isViewModeForCompanies)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForCompanies"
          defaultMessage="Şirketler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of companies model control
    if (editFormParams.companies.some((company) => !company.code || !company.vkn || !company.title))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfCompanies" defaultMessage="Şirket bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in companies array control
    if (!editFormParams.companies.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfCompanies"
          defaultMessage="En az bir şirket tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Related Contacts
    const isViewModeForRelatedContacts = Object.keys(rowModesModelRelatedContacts).length === 0;

    if (!isViewModeForRelatedContacts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForRelatedContacts"
          defaultMessage="İlgili Kişiler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of related contacts model control
    if (editFormParams.relatedContacts?.some((relatedContact) => !relatedContact.nameSurname || !relatedContact.email))
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredFieldsOfRelatedContacts"
          defaultMessage="İlgili Kişilerin bilgileri boş bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Email format validation for related contacts
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    if (editFormParams.relatedContacts?.some((relatedContact) => relatedContact.email !== relatedContact.email.trim()))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoWhitespace"
          defaultMessage="E-posta adresinin başında veya sonunda boşluk olamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    if (editFormParams.relatedContacts?.some((relatedContact) => /[A-Z]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.emailNoUpperCase" defaultMessage="E-posta adresi büyük harf içeremez!" />,
        {
          variant: 'warning',
        }
      );

    if (editFormParams.relatedContacts?.some((relatedContact) => /[çÇğĞıİöÖşŞüÜ]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoTurkishChars"
          defaultMessage="E-posta adresi Türkçe karakter içeremez!"
        />,
        {
          variant: 'warning',
        }
      );

    if (editFormParams.relatedContacts?.some((relatedContact) => !emailRegex.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.invalidEmailFormat" defaultMessage="Geçersiz e-posta formatı" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in related contacts array if effort contact type is only related contacts control
    if (
      editFormParams.effortMailContactType === EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS &&
      !editFormParams.relatedContacts?.length
    )
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfRelatedContactsIfEffortContactTypeIsOnlyRelatedContacts"
          defaultMessage="Sadece İlgili Kişiler seçili iken en az bir ilgili kişi tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Products
    const isViewModeForProducts = Object.keys(rowModesModelProducts).length === 0;

    if (!isViewModeForProducts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForProducts"
          defaultMessage="Ürünler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of products model control
    if (editFormParams.projects.some((project) => !project.id))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfProducts" defaultMessage="Ürün bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in products array control
    if (!editFormParams.projects.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfProjects"
          defaultMessage="En az bir ürün tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // Check that there is no more than one of the same product
    const projectIds = editFormParams.projects.map((project) => project.id);
    if (projectIds.filter((item, index) => projectIds.indexOf(item) !== index).length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.shouldNotBeMoreThanOneOfTheSameProduct"
          defaultMessage="Aynı üründen birden fazla olmamalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // continue
    try {
      setIsChanging(true);

      const response = await apiService.editBook(editFormParams);

      if (response.isSuccess) {
        const updatedBookId = editFormParams.id;
        const updatedBookTitle = editFormParams.companyName!;

        enqueueSnackbar(
          <FormattedMessage id="dashboard.updateSuccess" defaultMessage="Proje başarılı bir şekilde güncellendi." />,
          {
            variant: 'success',
          }
        );

        setBooks((currentBooks) =>
          currentBooks
            .map((book) => (book.id === updatedBookId ? { ...book, title: updatedBookTitle } : book))
            .sort((a, b) => a.title.localeCompare(b.title))
        );
        await getCompanies();
        onClickCancelHandler();

        // then
        setTimeout(() => {
          setSelectedCompanyId(updatedBookId);
        }, 1500);
      }
    } catch {
    } finally {
      setTimeout(() => {
        setIsChanging(false);
      }, 1500);
    }
  };

  const onClickApproveDeleteHandler = async () => {
    try {
      setIsDeleting(true);

      const response = await apiService.deleteBook(selectedBook.id);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage id="dashboard.deleteSuccess" defaultMessage="Proje başarılı bir şekilde silindi." />,
          {
            variant: 'success',
          }
        );
        getCompanies();
      }
    } catch {
    } finally {
      onClickCancelHandler();
    }
  };

  // sync helper functions
  const startDrag = (divider: '1-2' | '2-3') => (e: RPointerEvent<HTMLDivElement>) => {
    e.preventDefault();

    const startX = e.clientX;
    const containerWidth = sapRef.current?.offsetWidth ?? 1;
    const startSizes = { ...sizes };

    const onMove = (moveEvent: PointerEvent) => {
      const deltaPx = moveEvent.clientX - startX;
      const deltaPct = (deltaPx / containerWidth) * 100;

      if (divider === '1-2') {
        // col1 <-> col2, col3 fixed
        let col1 = startSizes.col1 + deltaPct;

        // limits
        const maxForCol1 = 100 - MIN_PERCENT - startSizes.col3;
        if (col1 < MIN_PERCENT) col1 = MIN_PERCENT;
        if (col1 > maxForCol1) col1 = maxForCol1;

        const col2 = 100 - startSizes.col3 - col1;

        setSizes({
          col1,
          col2,
          col3: startSizes.col3,
        });
      } else {
        // divider === '2-3' → col2 <-> col3, col1 fixed
        let col2 = startSizes.col2 + deltaPct;

        const maxForCol2 = 100 - MIN_PERCENT - startSizes.col1;
        if (col2 < MIN_PERCENT) col2 = MIN_PERCENT;
        if (col2 > maxForCol2) col2 = maxForCol2;

        const col3 = 100 - startSizes.col1 - col2;

        setSizes({
          col1: startSizes.col1,
          col2,
          col3,
        });
      }
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const resizeSapWithKeyboard = (divider: '1-2' | '2-3') => (event: RKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 2 : -2;

    setSizes((currentSizes) => {
      if (divider === '1-2') {
        const nextCol1 = Math.min(
          100 - MIN_PERCENT - currentSizes.col3,
          Math.max(MIN_PERCENT, currentSizes.col1 + delta)
        );

        return {
          col1: nextCol1,
          col2: 100 - currentSizes.col3 - nextCol1,
          col3: currentSizes.col3,
        };
      }

      const nextCol2 = Math.min(
        100 - MIN_PERCENT - currentSizes.col1,
        Math.max(MIN_PERCENT, currentSizes.col2 + delta)
      );

      return {
        col1: currentSizes.col1,
        col2: nextCol2,
        col3: 100 - currentSizes.col1 - nextCol2,
      };
    });
  };

  const scrollToSection = (ref: MutableRefObject<HTMLDivElement | null>) => {
    if (!ref.current) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    ref.current.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  };

  const handleScroll = () => setScrollUp(window.scrollY >= 180);

  const scrollToTop = useCallback(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, []);

  const handleBookSelect = (_event: SyntheticEvent<Element, Event>, value: ShortBook | null) => {
    setBookDetailsError(false);
    setPendingBookTitle(value?.title ?? null);
    if (!value) return setSelectedCompanyId(null);

    setSelectedCompanyId(value.id);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setEditFormParams((prevState) => ({ ...prevState, [event.target.id]: event.target.value }));

  const onPressTabKeyHandler = (event: RKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    event.stopPropagation();

    const input = event.currentTarget;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const insertion = insertTabAtSelection(input.value, selectionStart, selectionEnd);

    setEditFormParams((prevState) => ({ ...prevState, [input.id]: insertion.value }));

    requestAnimationFrame(() => {
      input.setSelectionRange(insertion.cursorPosition, insertion.cursorPosition);
    });
  };

  const onClickShowVersionsHandler = () => {
    if (!selectedCompanyId) {
      enqueueSnackbar(<FormattedMessage id="dashboard.noSelection" defaultMessage="Lütfen en az 1 proje seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenShowVersions(true);
  };

  const onClickEditHandler = () => {
    if (!selectedCompanyId) {
      enqueueSnackbar(<FormattedMessage id="dashboard.noSelection" defaultMessage="Lütfen en az 1 proje seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    setEdit(true);
  };

  const onClickDeleteHandler = () => {
    if (!selectedCompanyId) {
      enqueueSnackbar(<FormattedMessage id="dashboard.noSelection" defaultMessage="Lütfen en az 1 proje seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    setDeleteMode(true);
    setDeleteModalOpen(true);
  };

  const onClickCancelHandler = () => {
    setEdit(false);
    setRows(initialRows);
    setRowModesModel({});
    setDeleteMode(false);
    setIsDeleting(false);
    setSelectedUsedBanks([]);
    setSelectedGibType(null);
    setDeleteModalOpen(false);
    setSelectedCompanyId(null);
    setCompletedProjectIds([]);
    setProjectCompletionStatus('idle');
    setRowModesModelProducts({});
    setSelectedConnectionType(null);
    setRowModesModelRelatedContacts({});
    setRowsProducts(initialRowsProducts);
    setRowsRelatedContacts(initialRowsRelatedContacts);
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    if (!edit) {
      setRows(initialRows);
      setRowModesModel({});

      return initialRows[0];
    }

    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const renderScrollTags = () => (
    <Grid
      component="nav"
      aria-label={intl.formatMessage({ id: 'dashboard.modern.quickNavigation' })}
      container
      size="auto"
      gap={1}>
      <Grid>
        <Button
          sx={{ textTransform: 'none' }}
          size="small"
          onClick={() => scrollToSection(serverRef)}
          fullWidth
          variant="outlined"
          color="inherit">
          <FormattedMessage id="dashboard.tag.server" defaultMessage="#sunucu" />
        </Button>
      </Grid>
      <Grid>
        <Button
          sx={{ textTransform: 'none' }}
          size="small"
          onClick={() => scrollToSection(vpnRef)}
          fullWidth
          variant="outlined"
          color="inherit">
          <FormattedMessage id="dashboard.tag.vpn" defaultMessage="#vpn" />
        </Button>
      </Grid>
      <Grid>
        <Button
          sx={{ textTransform: 'none' }}
          size="small"
          onClick={() => scrollToSection(sapRef)}
          fullWidth
          variant="outlined"
          color="inherit">
          <FormattedMessage id="dashboard.tag.sap" defaultMessage="#sap" />
        </Button>
      </Grid>
    </Grid>
  );

  // useEffect hooks
  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    getCompanies();
    getUsers();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) {
      bookRequestIdRef.current += 1;
      setLoadingBookDetails(false);
      setPendingBookTitle(null);
      setSelectedBook(initialState);
      setEditFormParams(initialState);
      onClickCancelHandler();
      return;
    }

    getSpecificBook(selectedCompanyId);
  }, [selectedCompanyId, getSpecificBook]);

  useEffect(() => {
    const formattedCompanies: CompanyToBeEdited[] = rows.map((row) => ({
      code: row.code,
      vkn: row.vkn,
      title: row.title,
      dateOfAnnulment: row.dateOfAnnulment ? dayjs(row.dateOfAnnulment).toISOString() : null,
    }));

    setEditFormParams((prevState) => ({ ...prevState, companies: formattedCompanies }));
  }, [rows]);

  useEffect(() => {
    const formattedRelatedContacts: RelatedContactToBeEdited[] = rowsRelatedContacts.map((row) => ({
      nameSurname: row.nameSurname,
      email: row.email,
      phoneNumber: row.phoneNumber,
    }));

    setEditFormParams((prevState) => ({ ...prevState, relatedContacts: formattedRelatedContacts }));
  }, [rowsRelatedContacts]);

  useEffect(() => {
    const formattedProducts: ProductToBeEdited[] = rowsProducts.map((row) => ({
      id: Number(row.projectId) || 0,
      environmentTypeId: row.environmentTypeId ? Number(row.environmentTypeId) : null,
      liveDate: row.liveDate ? dayjs(row.liveDate).toISOString() : null,
      isOnHold: row.isOnHold,
      priority: row.priority || PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
      relatedContactId: row.relatedContactId || null,
      alternativeRelatedContactId: row.alternativeRelatedContactId || null,
      notes: row.notes || '',
      onHoldTotalDays: row.onHoldTotalDays !== null && row.onHoldTotalDays !== undefined ? row.onHoldTotalDays : 0,
    }));

    setEditFormParams((prevState) => ({ ...prevState, projects: formattedProducts }));
  }, [rowsProducts]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, pb: 3 }}>
      <ModernDashboardHeader
        companyName={
          isBookDetailsLoading
            ? pendingBookTitle
            : edit && updateCompanyName
              ? editFormParams.companyName
              : selectedBook.companyName
        }
        companyCount={selectedCompanyId ? rows.length : 0}
        productCount={selectedCompanyId ? rowsProducts.length : 0}
        contactCount={selectedCompanyId ? rowsRelatedContacts.length : 0}
        isEditing={edit}
        isCancelled={edit ? editFormParams.isCancelled : selectedBook.isCancelled}
        loading={isBookDetailsLoading}
      />

      {incompleteAdaptation ? (
        <Alert severity="info" icon={false}>
          <Typography>
            <FormattedMessage
              id="general.completeAdaptationsText"
              defaultMessage="Sayfayı görüntülemek için lütfen uyarlamaları tamamlayınız..! (Ürün Grupları, Ürünler, Bağlantı Türleri, Sunucu Tipleri, GİB Yöntemleri, Kullanılan Bankalar)"
            />
          </Typography>
        </Alert>
      ) : (
        <Grid container direction="column">
          <StickyActions isActive={true}>
            <Box sx={modernDashboardActionsSx}>
              <Grid
                justifyContent={deleteMode ? 'flex-end' : 'space-between'}
                container
                mb={0}
                sx={{
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: { xs: 'stretch', lg: 'center' },
                  gap: 1.25,
                }}>
                {edit ? (
                  <>
                    {isBookDetailsReady && renderScrollTags()}
                    <Grid
                      container
                      size="auto"
                      justifyContent="center"
                      sx={{
                        width: { xs: '100%', lg: 'auto' },
                        gap: 1,
                        flexWrap: 'wrap',
                        '& > *': { ml: '0 !important', flex: { xs: '1 1 auto', sm: '0 0 auto' } },
                      }}>
                      <Grid>
                        <Button
                          onClick={onClickSaveHandler}
                          fullWidth
                          disabled={isChanging}
                          variant="contained"
                          color="secondary"
                          sx={{ fontWeight: 700 }}>
                          <FormattedMessage id="dashboard.save" defaultMessage="Kaydet" />
                        </Button>
                      </Grid>
                      <Grid ml={0}>
                        <Button
                          onClick={onClickCancelHandler}
                          fullWidth
                          disabled={isChanging}
                          variant="outlined"
                          color="error"
                          sx={modernDangerButtonSx}>
                          <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                ) : deleteMode ? (
                  <>
                    <Grid>
                      <Button onClick={onClickEditHandler} fullWidth variant="contained" disabled={!isBookDetailsReady}>
                        <FormattedMessage id="dashboard.edit" defaultMessage="Düzenle" />
                      </Button>
                    </Grid>
                    <Grid ml={0}>
                      <Button
                        onClick={onClickDeleteHandler}
                        fullWidth
                        disabled={!isBookDetailsReady}
                        variant="outlined"
                        color="error"
                        sx={modernDangerButtonSx}>
                        <FormattedMessage id="dashboard.delete" defaultMessage="Sil" />
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <Autocomplete
                        size="small"
                        id="combo-box-books"
                        sx={{ minWidth: 0 }}
                        options={books}
                        loading={loadingBooks}
                        value={books.find((book) => book.id === selectedCompanyId) ?? null}
                        getOptionLabel={(option) => option.title}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        {...renderAutoCompleteLocaleText()}
                        onChange={handleBookSelect}
                        slotProps={{
                          paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              '& .MuiButtonBase-root': {
                                border: 'none',
                                backgroundColor: 'inherit',
                                width: '2.25rem',
                                height: '2.25rem',
                                padding: '0.25rem',
                                [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                                },
                              },
                            }}
                            label={<FormattedMessage id="dashboard.projects" defaultMessage="Projeler" />}
                            name="projectCompanyName"
                            fullWidth
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingBooks ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    {isBookDetailsReady && renderScrollTags()}
                    <Grid
                      container
                      size="auto"
                      justifyContent="center"
                      sx={{
                        width: { xs: '100%', lg: 'auto' },
                        gap: 1,
                        flexWrap: 'wrap',
                        '& > *': { ml: '0 !important', flex: { xs: '1 1 auto', sm: '0 0 auto' } },
                      }}>
                      <PermissionHandler requiredPermissionCodes={['version-compare']}>
                        <Grid>
                          <Button
                            onClick={onClickShowVersionsHandler}
                            fullWidth
                            disabled={!isBookDetailsReady}
                            variant="outlined"
                            color="inherit">
                            <FormattedMessage id="dashboard.editHistory" defaultMessage="Düzenleme Geçmişi" />
                          </Button>
                        </Grid>
                      </PermissionHandler>
                      <PermissionHandler requiredPermissionCodes={['update-book', 'update-full-book']}>
                        <Grid ml={0}>
                          <Button
                            onClick={onClickEditHandler}
                            fullWidth
                            disabled={!isBookDetailsReady}
                            variant="contained"
                            color="secondary">
                            <FormattedMessage id="dashboard.edit" defaultMessage="Düzenle" />
                          </Button>
                        </Grid>
                      </PermissionHandler>
                      <PermissionHandler requiredPermissionCodes={['delete-book']}>
                        <Grid ml={0}>
                          <Button
                            onClick={onClickDeleteHandler}
                            fullWidth
                            disabled={!isBookDetailsReady}
                            variant="outlined"
                            color="error"
                            sx={modernDangerButtonSx}>
                            <FormattedMessage id="dashboard.delete" defaultMessage="Sil" />
                          </Button>
                        </Grid>
                      </PermissionHandler>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </StickyActions>
          <Divider sx={{ marginY: 2.5 }} />
          {bookDetailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <FormattedMessage id="dashboard.modern.loadError" />
            </Alert>
          )}
          {isBookDetailsLoading ? (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                py: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}>
              <CircularProgress size={26} />
              <Typography color="text.secondary">
                <FormattedMessage id="dashboard.modern.loadingDetails" />
              </Typography>
            </Box>
          ) : !selectedCompanyId && !books.length && bookListStatus === 'loading' ? (
            <Alert severity="info" icon={<CircularProgress color="inherit" size={18} />}>
              <FormattedMessage id="dashboard.modern.loadingProjects" />
            </Alert>
          ) : !selectedCompanyId && !books.length && bookListStatus === 'error' ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={getCompanies}>
                  <FormattedMessage id="dashboard.modern.retry" />
                </Button>
              }>
              <FormattedMessage id="dashboard.modern.projectListError" />
            </Alert>
          ) : !selectedCompanyId && !books.length && bookListStatus === 'ready' ? (
            <Alert severity="info">
              <FormattedMessage id="dashboard.modern.noProjects" />
            </Alert>
          ) : !selectedCompanyId ? (
            <ModernDashboardEmptyState />
          ) : (
            <Grid container direction="row" spacing={2} sx={modernDashboardContentSx}>
              <ModernDashboardSection kind="overview" />
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="companyName">
                  {<FormattedMessage id="dashboard.companyName" defaultMessage="Firma" />}
                </FormLabel>
                <TextField
                  fullWidth
                  size="small"
                  onChange={
                    edit
                      ? updateCompanyName
                        ? onChangeHandler
                        : () =>
                            enqueueSnackbar(
                              <FormattedMessage
                                id="dashboard.noEdit"
                                defaultMessage="Bu alan düzenlemeye açık değildir!"
                              />,
                              { variant: 'warning' }
                            )
                      : () => {}
                  }
                  value={(edit && updateCompanyName ? editFormParams.companyName : selectedBook.companyName) || ''}
                  id="companyName"
                  slotProps={{
                    htmlInput: {
                      readOnly: !edit || !updateCompanyName,
                      onKeyDown: edit && updateCompanyName ? onPressTabKeyHandler : undefined,
                      style: { tabSize: TAB_SIZE },
                    },
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>{<FormattedMessage id="dashboard.companies" defaultMessage="Şirketler" />}</FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={`${intl.formatMessage({ id: 'dashboard.companies' })}: ${
                      selectedCompanyId ? rows.length : 0
                    }`}
                    aria-controls="dashboard-companies-content"
                    id="dashboard-companies-header">
                    {selectedCompanyId
                      ? `${rows.length} ${intl.formatMessage({ id: 'dashboard.company', defaultMessage: 'Şirket' })}`
                      : null}
                  </AccordionSummary>
                  <AccordionDetails id="dashboard-companies-content" aria-labelledby="dashboard-companies-header">
                    <StyledBox>
                      <ZebraDataGrid
                        rows={rows}
                        columns={columns}
                        columnVisibilityModel={{ none: false }}
                        disableColumnMenu
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        localeText={renderDataGridLocaleText()}
                        slots={{
                          toolbar: () => (
                            <>
                              <EditToolbar setRowModesModel={setRowModesModel} setRows={setRows} showButton={edit} />
                              {!edit && <CustomToolbar exportPremiumExcel={true} />}
                            </>
                          ),
                        }}
                        slotProps={{
                          toolbar: { setRows, setRowModesModel },
                        }}
                        hideFooter
                        disableRowSelectionOnClick
                        sx={{ border: `1px ${theme.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid` }}
                        disableAggregation
                        disableRowGrouping
                      />
                    </StyledBox>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>
                  {<FormattedMessage id="dashboard.relatedContacts" defaultMessage="İlgili Kişiler" />}
                </FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={`${intl.formatMessage({ id: 'dashboard.relatedContacts' })}: ${
                      selectedCompanyId ? rowsRelatedContacts.length : 0
                    }`}
                    aria-controls="dashboard-related-contacts-content"
                    id="dashboard-related-contacts-header">
                    {selectedCompanyId
                      ? rowsRelatedContacts.length
                        ? `${rowsRelatedContacts.length} ${intl.formatMessage({
                            id: 'project.table.relatedContact',
                            defaultMessage: 'İlgili Kişi',
                          })}`
                        : '-'
                      : null}
                  </AccordionSummary>
                  <AccordionDetails
                    id="dashboard-related-contacts-content"
                    aria-labelledby="dashboard-related-contacts-header">
                    <Box>
                      <RadioGroup
                        name="effort-mail-contact-type"
                        aria-labelledby="dashboard-effort-mail-contact-type-label"
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          justifyContent: { xs: 'flex-start', md: 'flex-end' },
                          alignItems: { xs: 'flex-start', md: 'center' },
                          gap: { xs: 0.5, md: 2 },
                        }}
                        aria-readonly={!edit}
                        value={edit ? editFormParams.effortMailContactType : selectedBook.effortMailContactType}
                        onChange={
                          edit
                            ? (e) =>
                                setEditFormParams((prevState) => ({
                                  ...prevState,
                                  effortMailContactType: Number(e.target.value),
                                }))
                            : () => {}
                        }>
                        <Typography id="dashboard-effort-mail-contact-type-label" mr={2}>
                          <FormattedMessage
                            id="project.effortMailContactTypeLabel"
                            defaultMessage="Efor Sürecinde E-Posta Kime Gitsin?"
                          />
                        </Typography>
                        <FormControlLabel
                          value={EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS}
                          control={<Radio disabled={!edit} />}
                          label={getEffortMailContactTypeTranslated(
                            EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS
                          )}
                        />
                        <FormControlLabel
                          value={EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER}
                          control={<Radio disabled={!edit} />}
                          label={getEffortMailContactTypeTranslated(
                            EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER
                          )}
                        />
                      </RadioGroup>
                    </Box>
                    <RelatedContactsEditTable
                      rows={rowsRelatedContacts}
                      setRows={setRowsRelatedContacts}
                      rowModesModel={rowModesModelRelatedContacts}
                      setRowModesModel={setRowModesModelRelatedContacts}
                      editMode={edit}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="note">{<FormattedMessage id="dashboard.note" defaultMessage="Notlar" />}</FormLabel>
                <RichTextOutlinedField
                  id="note"
                  ariaLabel={intl.formatMessage({ id: 'dashboard.note' })}
                  editable={edit}
                  value={(edit ? editFormParams.note : selectedBook.note) || ''}
                  onChange={(id, html) => {
                    setEditFormParams((prev) => ({ ...prev, [id]: html }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>{<FormattedMessage id="dashboard.products" defaultMessage="Ürünler" />}</FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={`${intl.formatMessage({ id: 'dashboard.products' })}: ${
                      selectedCompanyId ? rowsProducts.length : 0
                    }`}
                    aria-controls="dashboard-products-content"
                    id="dashboard-products-header">
                    {selectedCompanyId
                      ? `${rowsProducts.length} ${intl.formatMessage({
                          id: 'project.table.product',
                          defaultMessage: 'Ürün',
                        })}`
                      : null}
                  </AccordionSummary>
                  <AccordionDetails id="dashboard-products-content" aria-labelledby="dashboard-products-header">
                    {edit && updateFullBook && !isProductCompletionStatusReady && (
                      <Alert severity={projectCompletionStatus === 'error' ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
                        <FormattedMessage
                          id={
                            projectCompletionStatus === 'error'
                              ? 'dashboard.productEditRulesError'
                              : 'dashboard.productEditRulesLoading'
                          }
                        />
                      </Alert>
                    )}
                    <ProductsEditTable
                      rows={rowsProducts}
                      setRows={setRowsProducts}
                      rowModesModel={rowModesModelProducts}
                      setRowModesModel={setRowModesModelProducts}
                      products={products}
                      users={users}
                      environmentTypes={environmentTypes}
                      editMode={updateFullBook && edit && isProductCompletionStatusReady}
                      completedProjectIds={completedProjectIds}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <ModernDashboardSection kind="configuration" />
              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel htmlFor="dashboard-connection-type">
                  {<FormattedMessage id="dashboard.connectionTypes" defaultMessage="Bağlantı Türü" />}
                </FormLabel>
                <Autocomplete
                  id="dashboard-connection-type"
                  readOnly={!updateFullBook || !edit}
                  size="small"
                  options={connectionTypes}
                  loading={loadingConnectionTypes}
                  value={selectedConnectionType}
                  {...renderAutoCompleteLocaleText()}
                  onChange={(_, newValue: ConnectionType | null) => {
                    setSelectedConnectionType(newValue);

                    setEditFormParams((prevState) => ({ ...prevState, connectTypeId: newValue ? newValue.id : null }));
                  }}
                  getOptionLabel={(option: ConnectionType) => option.title}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                      variant="outlined"
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingConnectionTypes ? <CircularProgress color="inherit" size={20} /> : null}
                              {edit && params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel htmlFor="dashboard-gib-method">
                  {<FormattedMessage id="dashboard.gibMethod" defaultMessage="GİB Yöntemi" />}
                </FormLabel>
                <Autocomplete
                  id="dashboard-gib-method"
                  readOnly={!updateFullBook || !edit}
                  size="small"
                  options={gibTypes}
                  loading={loadingGibTypes}
                  value={selectedGibType}
                  {...renderAutoCompleteLocaleText()}
                  onChange={(_, newValue: GibType | null) => {
                    setSelectedGibType(newValue);

                    setEditFormParams((prevState) => ({ ...prevState, gibTypeId: newValue ? newValue.id : null }));
                  }}
                  getOptionLabel={(option: GibType) => option.title}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                      variant="outlined"
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingGibTypes ? <CircularProgress color="inherit" size={20} /> : null}
                              {edit && params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="dashboard-used-banks">
                  <FormattedMessage id="dashboard.banks" defaultMessage="Kullanılan Bankalar" />{' '}
                  {`${
                    selectedUsedBanks.length
                      ? `( ${selectedUsedBanks.length} ${intl.locale === 'tr' ? 'Banka' : 'Bank'} )`
                      : ''
                  }`}
                </FormLabel>
                <Autocomplete
                  id="dashboard-used-banks"
                  readOnly={!updateFullBook || !edit}
                  multiple
                  filterSelectedOptions
                  size="small"
                  options={usedBanks}
                  loading={loadingUsedBanks}
                  disableCloseOnSelect
                  value={selectedUsedBanks}
                  {...renderAutoCompleteLocaleText()}
                  onChange={(_, newValue: UsedBank[]) => {
                    setSelectedUsedBanks(newValue);

                    const usedBankIds = newValue.map((value) => value.id);
                    setEditFormParams((prevState) => ({ ...prevState, usedBankIds }));
                  }}
                  getOptionLabel={(option: UsedBank) => option.title}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          width: 'fit-content',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                        '& .MuiInputBase-root': {
                          height: 'auto',
                        },
                      }}
                      multiline
                      variant="outlined"
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUsedBanks ? <CircularProgress color="inherit" size={20} /> : null}
                              {edit && params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <ModernDashboardSection kind="access" />
              <Grid size={{ xs: 12 }}>
                <FormLabel>{<FormattedMessage id="dashboard.apiPassword" defaultMessage="API Şifresi" />}</FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={intl.formatMessage({ id: 'dashboard.apiPassword' })}
                    aria-controls="dashboard-api-password-content"
                    id="dashboard-api-password-header">
                    {selectedCompanyId ? (selectedBook.apiInformation ? '' : '-') : null}
                  </AccordionSummary>
                  <AccordionDetails id="dashboard-api-password-content" aria-labelledby="dashboard-api-password-header">
                    <RichTextOutlinedField
                      id="apiInformation"
                      ariaLabel={intl.formatMessage({ id: 'dashboard.apiPassword' })}
                      editable={edit}
                      value={(edit ? editFormParams.apiInformation : selectedBook.apiInformation) || ''}
                      onChange={(id, html) => {
                        setEditFormParams((prev) => ({ ...prev, [id]: html }));
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>
                  {<FormattedMessage id="dashboard.serviceApiInformations" defaultMessage="Sunucu API Bilgileri" />}
                </FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={intl.formatMessage({ id: 'dashboard.serviceApiInformations' })}
                    aria-controls="dashboard-service-api-informations-content"
                    id="dashboard-service-api-informations-header">
                    {selectedCompanyId ? (selectedBook.serviceApiInformation ? '' : '-') : null}
                  </AccordionSummary>
                  <AccordionDetails
                    id="dashboard-service-api-informations-content"
                    aria-labelledby="dashboard-service-api-informations-header">
                    <RichTextOutlinedField
                      id="serviceApiInformation"
                      ariaLabel={intl.formatMessage({ id: 'dashboard.serviceApiInformations' })}
                      editable={edit}
                      value={(edit ? editFormParams.serviceApiInformation : selectedBook.serviceApiInformation) || ''}
                      onChange={(id, html) => {
                        setEditFormParams((prev) => ({ ...prev, [id]: html }));
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>
                  {<FormattedMessage id="dashboard.RFCUserInformations" defaultMessage="RFC Kullanıcı Bilgileri" />}
                </FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={intl.formatMessage({ id: 'dashboard.RFCUserInformations' })}
                    aria-controls="dashboard-rfc-user-informations-content"
                    id="dashboard-rfc-user-informations-header">
                    {selectedCompanyId ? (selectedBook.rfcUserInformation ? '' : '-') : null}
                  </AccordionSummary>
                  <AccordionDetails
                    id="dashboard-rfc-user-informations-content"
                    aria-labelledby="dashboard-rfc-user-informations-header">
                    <RichTextOutlinedField
                      id="rfcUserInformation"
                      ariaLabel={intl.formatMessage({ id: 'dashboard.RFCUserInformations' })}
                      editable={edit}
                      value={(edit ? editFormParams.rfcUserInformation : selectedBook.rfcUserInformation) || ''}
                      onChange={(id, html) => {
                        setEditFormParams((prev) => ({ ...prev, [id]: html }));
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid ref={vpnRef} size={{ xs: 12 }}>
                <FormLabel htmlFor="vpnInformation">
                  {<FormattedMessage id="dashboard.vpnInformations" defaultMessage="VPN Bilgileri" />}
                </FormLabel>
                <RichTextOutlinedField
                  id="vpnInformation"
                  ariaLabel={intl.formatMessage({ id: 'dashboard.vpnInformations' })}
                  editable={edit}
                  value={(edit ? editFormParams.vpnInformation : selectedBook.vpnInformation) || ''}
                  onChange={(id, html) => {
                    setEditFormParams((prev) => ({ ...prev, [id]: html }));
                  }}
                />
              </Grid>
              <Grid ref={serverRef} size={{ xs: 12 }}>
                <FormLabel htmlFor="serverConnectionInformation">
                  {
                    <FormattedMessage
                      id="dashboard.serverConnectionInformations"
                      defaultMessage="Sunucu Bağlantı Bilgileri"
                    />
                  }
                </FormLabel>
                <RichTextOutlinedField
                  id="serverConnectionInformation"
                  ariaLabel={intl.formatMessage({ id: 'dashboard.serverConnectionInformations' })}
                  editable={edit}
                  value={
                    (edit ? editFormParams.serverConnectionInformation : selectedBook.serverConnectionInformation) || ''
                  }
                  onChange={(id, html) => {
                    setEditFormParams((prev) => ({ ...prev, [id]: html }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="serverExternalIp">
                  {
                    <FormattedMessage
                      id="dashboard.serverExternalIPInformation"
                      defaultMessage="Sunucu Dış IP Bilgisi"
                    />
                  }
                </FormLabel>
                <RichTextOutlinedField
                  id="serverExternalIp"
                  ariaLabel={intl.formatMessage({ id: 'dashboard.serverExternalIPInformation' })}
                  editable={edit}
                  value={(edit ? editFormParams.serverExternalIp : selectedBook.serverExternalIp) || ''}
                  onChange={(id, html) => {
                    setEditFormParams((prev) => ({ ...prev, [id]: html }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormLabel>{<FormattedMessage id="dashboard.financialSeal" defaultMessage="Mali Mühür" />}</FormLabel>
                <Accordion>
                  <AccordionSummary
                    sx={{
                      minHeight: 32,
                      '&.Mui-expanded': {
                        minHeight: 32,
                      },
                      '& .MuiAccordionSummary-content': {
                        my: 0,
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        my: 0,
                      },
                    }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-label={intl.formatMessage({ id: 'dashboard.financialSeal' })}
                    aria-controls="dashboard-financial-seal-content"
                    id="dashboard-financial-seal-header">
                    {selectedCompanyId ? (selectedBook.financialSeal ? '' : '-') : null}
                  </AccordionSummary>
                  <AccordionDetails
                    id="dashboard-financial-seal-content"
                    aria-labelledby="dashboard-financial-seal-header">
                    <RichTextOutlinedField
                      id="financialSeal"
                      ariaLabel={intl.formatMessage({ id: 'dashboard.financialSeal' })}
                      editable={edit}
                      value={(edit ? editFormParams.financialSeal : selectedBook.financialSeal) || ''}
                      onChange={(id, html) => {
                        setEditFormParams((prev) => ({ ...prev, [id]: html }));
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <ModernDashboardSection kind="sap" />
              <Grid size={{ xs: 12 }} className="dashboard-section-notice">
                <Alert severity="warning">
                  <FormattedMessage
                    id="dashboard.noRequestForRedText"
                    defaultMessage="KIRMIZI ile yazılan sistemlere request taşımıyoruz..!"
                  />
                </Alert>
              </Grid>
              <Box
                ref={sapRef}
                className="modern-dashboard-sap"
                display={{ xs: 'grid', lg: 'flex' }}
                width="100%"
                gap={2}
                sx={modernDashboardSapSx}>
                <Box
                  sx={{
                    width: { xs: '100%', lg: `calc(${sizes.col1}% - 10.67px)` },
                    position: 'relative',
                    border: '1px solid #ddd',
                    padding: 1,
                  }}>
                  <FormLabel htmlFor="sapDev">
                    <FormattedMessage id="dashboard.SAPDev" defaultMessage="SAP Dev" />
                  </FormLabel>
                  <ResizableRichTextOutlinedField
                    key="sap-dev-editor"
                    id="sapDev"
                    ariaLabel={intl.formatMessage({ id: 'dashboard.SAPDev' })}
                    editable={edit}
                    value={(edit ? editFormParams.sapDev : selectedBook.sapDev) || ''}
                    onChange={(id, html) => setEditFormParams((p) => ({ ...p, [id]: html }))}
                    minHeight={80}
                  />
                  <Box
                    key="sap-dev-resize-handle"
                    className="resize-handle"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      width: 8,
                      height: '100%',
                      cursor: 'col-resize',
                      touchAction: 'none',
                      zIndex: 10,
                    }}
                    role="separator"
                    tabIndex={0}
                    aria-orientation="vertical"
                    aria-label={intl.formatMessage({ id: 'dashboard.modern.resizeDevTest' })}
                    aria-valuemin={MIN_PERCENT}
                    aria-valuemax={100 - MIN_PERCENT - sizes.col3}
                    aria-valuenow={Math.round(sizes.col1)}
                    onKeyDown={resizeSapWithKeyboard('1-2')}
                    onPointerDown={startDrag('1-2')}
                  />
                </Box>

                <Box
                  sx={{
                    width: { xs: '100%', lg: `calc(${sizes.col2}% - 10.67px)` },
                    position: 'relative',
                    border: '1px solid #ddd',
                    padding: 1,
                  }}>
                  <FormLabel htmlFor="sapTest">
                    <FormattedMessage id="dashboard.SAPQA" defaultMessage="SAP QA" />
                  </FormLabel>
                  <ResizableRichTextOutlinedField
                    key="sap-test-editor"
                    id="sapTest"
                    ariaLabel={intl.formatMessage({ id: 'dashboard.SAPQA' })}
                    editable={edit}
                    value={(edit ? editFormParams.sapTest : selectedBook.sapTest) || ''}
                    onChange={(id, html) => setEditFormParams((p) => ({ ...p, [id]: html }))}
                    minHeight={80}
                  />
                  <Box
                    key="sap-test-resize-handle"
                    className="resize-handle"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      width: 8,
                      height: '100%',
                      cursor: 'col-resize',
                      touchAction: 'none',
                      zIndex: 10,
                    }}
                    role="separator"
                    tabIndex={0}
                    aria-orientation="vertical"
                    aria-label={intl.formatMessage({ id: 'dashboard.modern.resizeTestProd' })}
                    aria-valuemin={MIN_PERCENT}
                    aria-valuemax={100 - MIN_PERCENT - sizes.col1}
                    aria-valuenow={Math.round(sizes.col2)}
                    onKeyDown={resizeSapWithKeyboard('2-3')}
                    onPointerDown={startDrag('2-3')}
                  />
                </Box>

                <Box
                  sx={{
                    width: { xs: '100%', lg: `calc(${sizes.col3}% - 10.67px)` },
                    position: 'relative',
                    border: '1px solid #ddd',
                    padding: 1,
                  }}>
                  <FormLabel htmlFor="sapProd">
                    <FormattedMessage id="dashboard.SAPLive" defaultMessage="SAP Canlı" />
                  </FormLabel>
                  <ResizableRichTextOutlinedField
                    key="sap-prod-editor"
                    id="sapProd"
                    ariaLabel={intl.formatMessage({ id: 'dashboard.SAPLive' })}
                    editable={edit}
                    value={(edit ? editFormParams.sapProd : selectedBook.sapProd) || ''}
                    onChange={(id, html) => setEditFormParams((p) => ({ ...p, [id]: html }))}
                    minHeight={80}
                  />
                </Box>
              </Box>
              <ModernDashboardSection kind="notes" />
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="userNote">
                  {<FormattedMessage id="dashboard.userNote" defaultMessage="Kişisel Notlar" />}
                </FormLabel>
                <RichTextOutlinedField
                  id="userNote"
                  ariaLabel={intl.formatMessage({ id: 'dashboard.userNote' })}
                  editable={edit}
                  value={(edit ? editFormParams.userNote : selectedBook.userNote) || ''}
                  onChange={(id, html) => {
                    setEditFormParams((prev) => ({ ...prev, [id]: html }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  labelPlacement="end"
                  label={<FormattedMessage id="dashboard.isCancelled" defaultMessage="Proje İptal mi?" />}
                  control={
                    <Switch
                      checked={edit ? editFormParams.isCancelled : selectedBook.isCancelled}
                      disabled={!edit}
                      onChange={
                        edit
                          ? (event: ChangeEvent<HTMLInputElement>) =>
                              setEditFormParams((prevState) => ({ ...prevState, isCancelled: event.target.checked }))
                          : () => {}
                      }
                    />
                  }
                />
              </Grid>
            </Grid>
          )}
          <ShowVersions
            open={openShowVersions}
            selectedBookId={selectedCompanyId}
            setSelectedBookId={setSelectedCompanyId}
            handleClose={() => setOpenShowVersions(false)}
          />
          <Dialog
            open={deleteModalOpen}
            onClose={onClickCancelHandler}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
              <FormattedMessage id="dashboard.areYouSure" defaultMessage="Projeyi Silmek İstediğinize Emin Misiniz?" />
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <FormattedMessage
                  id="dashboard.confirmDelete"
                  defaultMessage="Bu işlem onaylandıktan sonra proje veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" disabled={isDeleting} onClick={onClickCancelHandler}>
                <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
              </Button>
              <Button variant="contained" disabled={isDeleting} color="error" onClick={onClickApproveDeleteHandler}>
                <FormattedMessage id="dashboard.delete" defaultMessage="Sil" />
              </Button>
            </DialogActions>
          </Dialog>
          {scrollUp && (
            <Button
              sx={{
                position: 'fixed',
                bottom: 96,
                right: 24,
                height: 50,
                zIndex: (theme) => theme.zIndex.speedDial,
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],

                '&:hover': {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200],
                },
              }}
              aria-label={intl.formatMessage({ id: 'dashboard.scrollToTop' })}
              onClick={scrollToTop}>
              <ExpandLessIcon />
            </Button>
          )}
          <Backdrop
            sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            role="status"
            aria-live="polite"
            aria-label={intl.formatMessage({ id: 'autocomplete.loadingText' })}
            open={isChanging || isDeleting}>
            <img src={loaderLogoSrc} alt="" />
            <CircularProgress color="inherit" />
          </Backdrop>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
